import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import {
	DataPerformanceSummaryRequestBodyObjectType,
	DataPerformanceSummaryType,
} from 'webapp/enums/enums';
import { copyObject, serializeMetricSetupData, utf8_to_b64 } from 'webapp/utils/common';

import type { DataPerformanceSummaryObject as DataPerformanceSummaryObjectBase } from 'webapp/models-custom/data-performance-summary/base';
import DataPerformanceSummaryBase from 'webapp/models-custom/data-performance-summary/base';
import DateRange from 'webapp/models-custom/date-range';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import MetricSetup from 'webapp/models-custom/metric-setup';

export interface DataPerformanceSummaryObject extends DataPerformanceSummaryObjectBase {
	dataSelection: APIData.GenerativeAI.DataPerformance.SummaryDataSelection;
}

export default class DataPerformanceSummaryDynamic extends DataPerformanceSummaryBase {
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;

	objectType = DataPerformanceSummaryType.DYNAMIC;

	@tracked private isRangeValid = false;
	@tracked requestedParamHash = '';

	@tracked dataSelection?: APIData.GenerativeAI.DataPerformance.SummaryDataSelection;

	constructor(model: DataPerformanceSummaryObject) {
		super(model);

		const ds = model.dataSelection;

		if (ds.metricSetup?.key && ds.dateRange?.range) {
			this.dataSelection = copyObject(ds);
		}
	}

	get paramHash() {
		return utf8_to_b64(JSON.stringify(this.summariesRequestBodyBase));
	}

	@cached
	get object() {
		const dataSelection = this.dataSelection;

		if (dataSelection?.metricSetup.key) {
			const metricSetup = new MetricSetup(dataSelection.metricSetup);
			return metricSetup;
		}

		return null;
	}

	@cached
	get dateRangeFormula() {
		return this.dataSelection?.dateRange;
	}

	get title() {
		const object = this.object;
		return (
			(this.singleOutputMetricSetupData
				? object?.selectedMetricTitle
				: object?.metric?.name) || ''
		);
	}

	@cached
	get sources() {
		const source = this.object?.source;

		if (source) {
			return [source];
		}

		return [];
	}

	get trend() {
		return this.sanitizedSummary?.trend || this.defaultTrend;
	}

	get isDataIncomplete() {
		return undefined;
	}

	get isDataEmpty() {
		return undefined;
	}

	get trackingObjectType() {
		return 'metric';
	}

	get trackingMetricsNames() {
		return [this.title];
	}

	get summariesRequestBodyBase(): APIData.GenerativeAI.DataPerformance.SummariesRequest | null {
		if (this.dataSelection && this.isRangeValid) {
			const metricSetup = serializeMetricSetupData(this.dataSelection.metricSetup)!;
			return {
				typeObject: DataPerformanceSummaryRequestBodyObjectType.DYNAMIC,
				dataSelection: {
					metricSetup,
					dateRange: this.dataSelection?.dateRange,
				},
			};
		}

		return null;
	}

	get isValidMetrickey() {
		return !!this.object?.key;
	}

	@cached
	get metricSetupData() {
		return this.object?.metricSetupData || null;
	}

	@cached
	get singleOutputMetricSetupData() {
		if (this.object?.isSingleOutput) {
			return this.metricSetupData;
		}

		return null;
	}

	@cached
	get objectDateRange(): DateRange | null {
		if (this.dataSelection?.dateRange) {
			return DateRange.createFromFormula(this.dataSelection?.dateRange);
		}

		return null;
	}

	override get canRequestSummaries() {
		const hasValidDataSelectionAndRange =
			this.isValidMetrickey && !!this.objectDateRange && this.isRangeValid;

		return super.canRequestSummaries && hasValidDataSelectionAndRange;
	}

	override get canRequestForecast() {
		const hasValidDataSelectionAndRange = true; // TODO Forecasts have granualtion and so on

		return super.canRequestForecast && hasValidDataSelectionAndRange;
	}

	override checkAndLoadAdditionalData() {
		const objectDateRange = this.objectDateRange;

		if (this.isValidMetrickey && objectDateRange) {
			const metricData = this.object?.metricData,
				dynamicSummaryLimitations = new MetricLimitations({
					metrics: [metricData!], // checked in isValidMetrickey
					dateRange: objectDateRange,
				});

			this.summariesLoading = true; // Keep component in loading state
			void dynamicSummaryLimitations.limitationsLoaded
				?.then(() => {
					const dynamicSummaryLimitationRange =
							dynamicSummaryLimitations.limitationsForSelectedRange,
						isRangeValid = !dynamicSummaryLimitationRange?.disabled;

					if (isRangeValid) {
						this.isRangeValid = true;
					}
				})
				.finally(() => {
					this.summariesLoading = false; // reset before calling checkAndLoadAdditionalData cause it wouldn't make a call to the BE
					this.setupAndLoadAdditionalData();
				});
		} else {
			this.setupAndLoadAdditionalData();
		}
	}

	setupAndLoadAdditionalData() {
		this.requestedParamHash = this.paramHash;
		this.loadAdditionalData();
	}

	resetTrend() {
		if (this.summaries.length) {
			const summaries = copyObject(this.summaries);
			summaries[0]!.trend = this.defaultTrend;
			this.summaries = summaries;
		}
	}
}
